<template>
  <parent>
    <form-wrapper
      id="formWrapper"
      full-footer
      full-width
      full-height
    >
      <template v-slot:body>
        <loading-page v-if="loading" />
        <div
          v-else
          class="text-center w-100"
          :class="{ 'm-h-100': totalCount > 5 }"
        >
          <div v-if="errors.length">
            <p
              v-for="error in errors"
              :key="error"
            >
              {{ error }}
            </p>
          </div>
          <template v-else>
            <projects-header
              :has-projects="hasProjects"
              :show-welcome-message="showWelcomeMessage"
            />
            <div class="m-auto mb-1 col-12 col-sm-6 col-lg-4 col-xl-3">
              <b-button
                v-if="$can('create', 'projects', 'captain')"
                variant="bg-primary"
                class="w-100 mt-2 mb-1"
                @click="$router.push({ name: 'project-add' })"
              >
                <GjIcon
                  class="icon-left"
                  name="Plus"
                />
                Create Project
              </b-button>
              <template v-if="hasProjects || filter">
                <seperator class="mt-1" />
                <div class="position-relative">
                  <div
                    v-if="totalCount > pageSize || filter"
                    class="d-flex justify-content-center search-bar"
                  >
                    <b-input-group class="mb-2">
                      <b-input-group-prepend is-text>
                        <b-icon-search />
                      </b-input-group-prepend>
                      <b-form-input
                        id="filterInput"
                        v-model.trim="filter"
                        type="search"
                        class="m-0"
                        placeholder="Type to Search"
                        @keyup.enter="fetchProjects"
                      />
                    </b-input-group>
                  </div>
                  <div v-if="hasProjects">
                    <template v-for="project in projects">
                      <div
                        :key="project.id"
                        class="project-button d-sm-flex my-1 align-items-center"
                      >
                        <b-button
                          variant="bg-white"
                          class="w-100"
                          @click="onSelect(project)"
                        >
                          <span class="rounder-image icon-left" />
                          {{ project.name }}
                          <GjIcon
                            class="icon-left ml-auto"
                            name="ArrowRight"
                          />
                        </b-button>
                        <template v-if="!fetchingUser && showSetProject">
                          <template
                            v-if="updatingProject && projectId === project.id"
                          >
                            <span
                              class="default"
                            >Setting as default <b-spinner
                              class="spinner"
                            /></span>
                          </template>
                          <template v-else-if="projectId == project.id">
                            <span class="default">
                              <a
                                v-b-popover.hover.focus="
                                  'This project is set as your default project.'
                                "
                              ><GjIcon
                                name="Check"
                              /></a>
                              <a
                                class="text-primary edit"
                                @click="
                                  updateDefaultProject(
                                    noDefaultProject ? false : true,
                                  )
                                "
                              >{{ noDefaultProject ? 'Cancel' : 'Change' }}</a>
                            </span>
                          </template>
                          <template v-else-if="noDefaultProject">
                            <a
                              class="default text-primary"
                              @click="setDefault(project.id)"
                            >
                              Set as default
                            </a>
                          </template>
                        </template>
                      </div>
                    </template>
                  </div>
                  <div v-else>
                    <h6>No projects matched your search criteria.</h6>
                  </div>
                  <table-busy
                    v-if="fetchingProjects"
                    style="top: 0; height: 100%;"
                  />
                </div>

                <div
                  v-if="totalCount > pageSize"
                  class="default-pagination flex-column mt-2"
                >
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalCount"
                    :per-page="pageSize"
                    first-number
                    last-number
                  />
                </div>
              </template>
              <template v-else>
                <h6 class="mt-1">
                  No projects found for this specific organization.
                </h6>
              </template>
            </div>
          </template>
        </div>
      </template>
      <template v-slot:footer>
        <b-button
          variant="bg-white"
          @click="$router.push({ name: 'organization', query: $route.query })"
        >
          Go Back
        </b-button>
      </template>
    </form-wrapper>
  </parent>
</template>

<script>
import Seperator from '@/components/seperator/Seperator.vue'
import ProjectsHeader from './parts/Header.vue'
import redirectError from '@/services/redirectError'
import { BIconSearch } from 'bootstrap-vue'

export default {
  components: { Seperator, ProjectsHeader, BIconSearch },
  data() {
    return {
      organizationId: 0,
      errors: [],
      showWelcomeMessage: null,
      currentPage: 1,
      loading: true,
      projectId: null,
      filter: '',
      typingTimer: null,
      doneTypingInterval: 500,
    }
  },
  computed: {
    hasProjects() {
      return this.projects.length !== 0
    },
    fetchingProjects() {
      return this.$store.state.projects.fetchingProjects
    },
    projects() {
      return this.$store.state.projects.projects
    },
    resultInfo() {
      return this.$store.state.projects.resultInfo
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    pageSize() {
      return this.resultInfo.pageSize || 5
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    noDefaultProject() {
      return this.$store.state.users.noDefaultProject
    },
    showSetProject() {
      return this.user.defaultOrganizationId === this.organizationId
    },
    user() {
      return this.$store.state.users.user
    },
    updatingProject() {
      return this.$store.state.users.updatingUser
    },
    fetchingUser() {
      return this.$store.state.users.fetchingUser
    },
  },
  watch: {
    currentPage() {
      this.fetchProjects()
    },
    filter() {
      clearTimeout(this.typingTimer)
      this.typingTimer = setTimeout(() => {
        this.onChangeSearch()
      }, this.doneTypingInterval)
    },
  },
  created() {
    const welcomeMessage = localStorage.getItem('welcomeMessage')
    if (!this.isNullOrEmpty(welcomeMessage)) {
      this.showWelcomeMessage = welcomeMessage
      localStorage.removeItem('welcomeMessage')
    }

    this.organizationId = this.$store.state.app.organizationId
    this.fetchProjects()
    this.fetchUser()
  },
  methods: {
    onSelect(project) {
      this.$store.commit('app/SET_PROJECT_ID', project.id)
      this.$store.commit('app/SET_PROJECT_NAME', project.name)
      this.$store.commit('app/SET_PROJECT_PAGE', this.pageIndex)
      this.loading = true
      this.$store.commit('appConfig/UPDATE_NAV_MENU_HIDDEN', false)
      this.$router.push({
        name: 'distributions',
        params: { projectId: project.id },
      })
    },
    fetchUser() {
      this.$store.dispatch('users/fetchUser')
        .then(() => {
          this.projectId = this.user.defaultProjectId
        })
    },
    fetchProjects() {
      this.$store.dispatch('projects/fetchProjects', {
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        search: !this.isNullOrEmpty(this.filter) ? this.filter : null,
      })
        .then(res => {
          if (!res.data.success) {
            this.errorMessage = res.errors
          }
        })
        .catch(error => {
          if (error.response) {
            const { status } = error.response
            if (status >= 500) {
              redirectError(error)
            }
            this.errors = error.response.data.errors
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    setDefault(id) {
      this.projectId = id
      this.updateUser()
    },
    updateUser() {
      this.$store.dispatch('users/updateUser', {
        defaultOrganizationId: this.user.defaultOrganizationId,
        defaultProjectId: this.projectId,
      })
        .catch(error => {
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while updating user.')
        })
    },
    updateDefaultProject(val) {
      this.$store.commit('users/SET_NO_DEFAULT_PROJECT', val)
    },
    onChangeSearch() {
      if (this.currentPage === 1) {
        this.fetchProjects()
      } else {
        this.currentPage = 1
      }
    },
  },
}
</script>

<style lang="scss" scoped>
>>> .form-wrapper__inner {
  background-color: red;
}

.project-button {
  position: relative;
  .default {
    text-align: left;
    @media (min-width: 576px) {
      position: absolute;
      left: 100%;
      width: 140px;
      margin-left: 15px;
    }

    @media (max-width: 575.8px) {
      margin-top: 8px;
    }
    .spinner {
      width: 1rem;
      height: 1rem;
      margin-left: 3px;
    }
    .edit {
      margin-left: 7px;
    }
  }
}
.m-h-100 {
  min-height: 100%;
}
</style>
